import styled from 'styled-components';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import SwitchToggle from '../components/SwitchToggle';
import NonImage from '../Images/image.png';
import ConfirmationModal from '../components/ConfirmationModal';
import { getCoursesAdmin, updateCourse, deleteCourse } from '../redux/httpRequests';
import { setLoading, resetLoading } from '../redux/loadingSlice';
import { mobile } from '../responsive';

const Container = styled.div`
  height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Wrapper = styled.div`
  display: flex;
  padding: 20px;
  height: 80vh;
  width: 75vw;
  justify-content: center;
  flex-direction: column;
  ${mobile({width: '95vw', padding: '0px', marginLeft: '0px', height: '85vh'})}
`;
const ImgContainer = styled.div` display: flex; align-items: center; `;
const Image = styled.img`
  height: 45px; width: 45px; margin-right: 10px; object-fit: cover; border-radius: 5px; border: 1px solid #ddd;
`;
const CellAction = styled.div` display: flex; align-items: center; gap: 15px; `;
const ViewButton = styled.div`
  padding: 2px 5px; border-radius: 5px; color: darkblue; border: 1px solid rgba(0,0,139,.6); cursor: pointer;
  &:hover{ background: rgba(0,0,139,.6); color: #fff; }
`;
const ApplyButton = styled.div`
  padding: 2px 5px; border-radius: 5px; color: darkgreen; border: 1px solid rgba(0,139,53,.6); cursor: pointer;
  &:hover{ background: rgba(0,139,53,.6); color: #fff; }
`;
const DeleteButton = styled.div`
  padding: 2px 5px; border-radius: 5px; color: crimson; border: 1px solid rgba(220,20,60,.6); cursor: pointer;
  &:hover{ background: rgba(220,20,60,.6); color: #fff; }
`;
const Top = styled.div` display: flex; justify-content: flex-end; `;
const NewButton = styled(Link)`
  padding: 10px; border-radius: 5px; color: #fff; border: 1px solid transparent; background: rgba(57,179,85,.6);
  text-decoration: none; margin-bottom: 30px; font-weight: 800;
  &:hover{ background:#fff; color: rgba(57,179,85,.6); border:1px solid rgba(57,179,85,.6); }
`;
const Input = styled.input`
  width: 60px; height: 24px; margin-left: 12px; margin-bottom: 3px; border-radius: 5px; padding: 3px 6px; border: 1px solid #bbb;
  &:focus{ outline: none; border: 1px solid #00bcd4; }
`;
const localizedTextsMap = {
  columnMenuUnsort: "Desclasificar", columnMenuSortAsc: "Ordenar ascendentemente", columnMenuSortDesc: "Ordenar decrecientemente",
  columnMenuFilter: "Filtro", columnMenuHideColumn: "Ocultar", columnMenuShowColumns: "Mostrar columnas",
};

export default function CourseList() {
  const dispatch = useDispatch();
  const courses = useSelector((s) => s.course.courses || []);

  const [discModalOpen, setDiscModalOpen] = useState(false);
  const [discRow, setDiscRow] = useState(null);
  const [discValue, setDiscValue] = useState('');

  const [delModalOpen, setDelModalOpen] = useState(false);
  const [delRow, setDelRow] = useState(null);

  useEffect(() => { dispatch(getCoursesAdmin()); }, [dispatch]);

  const doUpdate = (id, payload) => { dispatch(updateCourse({ id, course: payload })); };

  const toggleStatus = (id, bool) => {
    const c = courses.find(x => x._id === id); if (!c) return;
    doUpdate(id, { ...c, status: bool ? 'active' : 'inactive' });
  };

  const toggleDiscount = (id, bool) => {
    const c = courses.find(x => x._id === id); if (!c) return;
    doUpdate(id, { ...c, discount: bool, discountAmount: bool ? (c.discountAmount || 0) : 0 });
    if (!bool) {            // si lo apago, limpio edición local
        setDiscRow(null);
        setDiscValue('');
        setDiscModalOpen(false);
    }
  };

  const openDiscount = (row) => { setDiscRow(row); setDiscModalOpen(true); };

  const focusDiscount = (row) => {
    // Al enfocar/clickear el input, esta fila pasa a ser la “activa”
    setDiscRow(row);
    setDiscValue(String(row.discountAmount ?? ''));
  };
  
  const confirmDiscount = async () => {
    if (!discRow) return;
    const id = discRow._id;
    const base = courses.find(x => x._id === id);
    const amount = Math.max(0, Math.min(99, parseInt(discValue || '0', 10)));

    await dispatch(updateCourse({ id, course: { ...base, discountAmount: amount } }));

    setDiscModalOpen(false);
    setDiscRow(null);
    setDiscValue('');
  };

  const askDelete = (row) => { setDelRow(row); setDelModalOpen(true); };
  const confirmDelete = async () => {
    try {
      dispatch(setLoading());
      await dispatch(deleteCourse(delRow._id));
    } finally {
      setDelModalOpen(false); setDelRow(null); dispatch(resetLoading());
    }
  };

  const columns = [
    { field: '_id', headerName: 'ID', width: 240 },
    { field: 'title', headerName: 'Título', width: 320, renderCell: (p) => (
      <ImgContainer><Image src={p.row.thumbnail || NonImage} alt="" />{p.row.title}</ImgContainer>
    )},
    { field: 'duration', headerName: 'Duración (min)', width: 130, sortable: true },
    { field: 'price', headerName: 'Precio', width: 120, sortable: true, renderCell: (p) => <>$ {new Intl.NumberFormat().format(p.row.price || 0)}</> },
    { field: 'status', headerName: 'Activo', width: 110, renderCell: (p) => (
      <SwitchToggle inStock={p.row.status === 'active'} id={p.row._id} fn={(id, b) => toggleStatus(id, b)} />
    )},
    { field: 'discount', headerName: 'Descuento (%)', width: 250, renderCell: (p) => (
      <>
        <SwitchToggle style={{marginRight:12}} inStock={!!p.row.discount} id={p.row._id} fn={(id, b) => toggleDiscount(id, b)} />
        {p.row.discount ? (
          <>
            <Input
              type="number" min="0" max="99"
              value={discRow?._id === p.row._id ? discValue : (p.row.discountAmount ?? '')}
              onFocus={() => focusDiscount(p.row)}
              onClick={() => focusDiscount(p.row)}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, ''); // sólo números
                setDiscValue(v.slice(0, 2));                  // hasta 2 dígitos
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {            // Enter para aplicar rápido
                    focusDiscount(p.row);
                    setDiscModalOpen(true);
                }
              }}
            />
            <ConfirmationModal
              open={discModalOpen && discRow?._id === p.row._id}
              close={() => { setDiscModalOpen(false); setDiscRow(null); }}
              children={
               <ApplyButton onClick={() => {
                 setDiscRow(p.row);        // sólo setear la fila
                 setDiscModalOpen(true);   // y abrir modal
               }}>
                 Aplicar
               </ApplyButton>
              }
              title="Aplicar descuento" text="¿Confirmás el porcentaje de descuento?"
              fn={confirmDiscount}
            />
          </>
        ) : (
          <>
            <Input type="number" disabled />
            <ApplyButton style={{borderColor:'#bebebe',color:'#bebebe',cursor:'default'}} disabled>Aplicar</ApplyButton>
          </>
        )}
      </>
    )},
    { field: 'action', headerName: 'Acciones', width: 200, sortable: false, renderCell: (p) => (
      <CellAction>
        <Link to={`/courses/${p.row._id}`} style={{ textDecoration: 'none' }}><ViewButton>Editar</ViewButton></Link>
        <ConfirmationModal
          open={delModalOpen && delRow?._id === p.row._id}
          close={() => { setDelModalOpen(false); setDelRow(null); }}
          children={<DeleteButton onClick={() => askDelete(p.row)}>Eliminar</DeleteButton>}
          title="Eliminar curso" text="¿Seguro? Esta acción no se puede deshacer."
          fn={confirmDelete}
        />
      </CellAction>
    )},
  ];
// actualiza
  return (
    <Container>
      <Wrapper>
        <Top><NewButton to="/courses/new">+ Nuevo Curso</NewButton></Top>
        <DataGrid
          rows={courses}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          getRowId={(r) => r._id}
          sx={{ borderRadius:'10px', marginLeft:'20px' }}
          localeText={localizedTextsMap}
        />
      </Wrapper>
    </Container>
  );
}